import { useEffect, useState } from "react";
import { X, Upload, Image as ImageIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  FilterBar,
  KorwilBadge,
  Pagination,
  Progress,
  SectionTitle,
  SelectFilter,
  StatusBadge,
  TableShell,
  Td,
  TextFilter,
  useDeferredFilters,
  SearchButton,
  Th,
  fmtDate,
} from "@/components/Shared";

function SekolahDetail({ npsn, onClose }) {
  const { user } = useAuth();
  const canViewProgressNotes = ["SUPER_ADMIN", "KORWIL", "ADMIN_KORWIL"].includes(user?.role);
  const [d, setD] = useState(null);
  const [progressNotes, setProgressNotes] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [progressError, setProgressError] = useState("");
  const [progressStatus, setProgressStatus] = useState("TERKENDALA");
  const [progressText, setProgressText] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [progressPhoto, setProgressPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const loadProgressNotes = () => {
    if (!canViewProgressNotes) return;
    setProgressLoading(true);
    api.get(`/sekolah/${npsn}/progress-notes`)
      .then(({ data }) => setProgressNotes(data.items || []))
      .catch((e) => setProgressError(e?.response?.data?.detail || "Gagal memuat catatan progres"))
      .finally(() => setProgressLoading(false));
  };

  useEffect(() => {
    api.get(`/sekolah/${npsn}`).then(({ data }) => setD(data)).catch(() => setD(null));
    loadProgressNotes();
  }, [npsn, canViewProgressNotes]);

  const saveProgressNote = async (e) => {
    e.preventDefault();
    setProgressError("");
    if (!progressText.trim()) {
      setProgressError("Catatan kendala/progres wajib diisi.");
      return;
    }
    if (progressPhoto && !["image/jpeg", "image/png", "image/webp"].includes(progressPhoto.type)) {
      setProgressError("Foto harus berformat JPG, PNG, atau WEBP.");
      return;
    }
    if (progressPhoto && progressPhoto.size > 8 * 1024 * 1024) {
      setProgressError("Ukuran foto maksimal 8 MB.");
      return;
    }
    const form = new FormData();
    form.append("status", progressStatus);
    form.append("catatan", progressText.trim());
    if (plannedDate) form.append("tanggal_rencana", plannedDate);
    if (progressPhoto) form.append("file", progressPhoto);
    setSavingProgress(true);
    try {
      await api.post(`/sekolah/${npsn}/progress-notes`, form);
      setProgressText("");
      setPlannedDate("");
      setProgressPhoto(null);
      const fileInput = document.getElementById("school-progress-photo");
      if (fileInput) fileInput.value = "";
      loadProgressNotes();
    } catch (e) {
      setProgressError(e?.response?.data?.detail || "Gagal menyimpan catatan progres.");
    } finally {
      setSavingProgress(false);
    }
  };

  const viewProgressPhoto = async (noteId) => {
    setProgressError("");
    try {
      const { data } = await api.get(`/sekolah/${npsn}/progress-notes/${noteId}/photo`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setPhotoPreview((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return url;
      });
    } catch (e) {
      setProgressError(e?.response?.data?.detail || "Foto tidak dapat dibuka.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" onClick={onClose}>
      <div
        data-testid="sekolah-detail-drawer"
        className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-800 bg-[#0d121c] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-white">{d?.sekolah || "Memuat..."}</h3>
            <div className="mt-1 font-mono-data text-xs text-blue-300">NPSN {npsn}</div>
          </div>
          <button data-testid="close-sekolah-detail" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        {d && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
              {[
                ["Korwil", d.korwil],
                ["Korda", d.korda || "-"],
                ["Provinsi", d.propinsi],
                ["Kabupaten/Kota", d.kabupaten_kota],
                ["Kecamatan", d.kecamatan || "-"],
                ["Total Unit", d.total_unit],
                ["Selesai", d.selesai],
                ["Progress", `${d.progress}%`],
              ].map(([l, v]) => (
                <div key={l} className="rounded border border-slate-800 bg-slate-900/70 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
                  <div className="mt-0.5 truncate text-slate-200">{v}</div>
                </div>
              ))}
            </div>
            <Progress value={d.progress} className="mt-4" />

            {canViewProgressNotes && (
              <section className="mt-6 rounded-lg border border-slate-700 bg-slate-900/50 p-4" data-testid="school-progress-notes">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-display text-base font-semibold text-white">Catatan Status Progres Instalasi</h4>
                    <p className="mt-1 text-xs text-slate-400">Khusus Super Admin, Korwil, dan Admin Korwil. Tambahkan kendala, tindak lanjut, atau rencana instalasi.</p>
                  </div>
                  <span className="rounded bg-blue-500/15 px-2 py-1 text-[10px] font-semibold text-blue-300">{progressNotes.length} catatan</span>
                </div>

                <form onSubmit={saveProgressNote} className="mt-4 space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block text-xs text-slate-400">
                      Status progres
                      <select value={progressStatus} onChange={(e) => setProgressStatus(e.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100">
                        <option value="TERKENDALA">Terkendala</option>
                        <option value="BELUM ADA TEKNISI">Belum Ada Teknisi</option>
                        <option value="KOORDINASI SEKOLAH">Koordinasi dengan Sekolah</option>
                        <option value="JADWAL ULANG">Jadwal Ulang</option>
                        <option value="INFO PROGRES">Info Progres</option>
                        <option value="LAINNYA">Lainnya</option>
                      </select>
                    </label>
                    <label className="block text-xs text-slate-400">
                      Rencana tindak lanjut / tanggal instalasi (opsional)
                      <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
                    </label>
                  </div>
                  <label className="block text-xs text-slate-400">
                    Catatan kendala / progres
                    <textarea value={progressText} onChange={(e) => setProgressText(e.target.value)} maxLength={3000} rows={3} required placeholder="Contoh: sekolah bersedia instalasi besok; teknisi belum tersedia; akses lokasi terkendala..." className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500" />
                  </label>
                  <label className="block text-xs text-slate-400">
                    Foto bukti kendala (JPG/PNG/WEBP, maks. 8 MB)
                    <input id="school-progress-photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setProgressPhoto(e.target.files?.[0] || null)} className="mt-1 block w-full text-xs text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-700 file:px-3 file:py-2 file:text-slate-100" />
                  </label>
                  {progressError && <p role="alert" className="rounded border border-red-700/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">{progressError}</p>}
                  <button type="submit" disabled={savingProgress} className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
                    {savingProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {savingProgress ? "Menyimpan..." : "Simpan Catatan Progres"}
                  </button>
                </form>

                <div className="mt-5 space-y-3">
                  {progressLoading && <p className="text-xs text-slate-400">Memuat riwayat catatan...</p>}
                  {!progressLoading && progressNotes.length === 0 && <p className="text-xs text-slate-500">Belum ada catatan progres instalasi.</p>}
                  {progressNotes.map((note) => (
                    <article key={note.id} className="rounded border border-slate-700 bg-slate-950/60 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="rounded bg-amber-500/15 px-2 py-1 text-[10px] font-semibold text-amber-300">{note.status}</span>
                        <span className="text-[10px] text-slate-500">{note.created_at ? new Date(note.created_at).toLocaleString("id-ID") : "-"}</span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-200">{note.catatan}</p>
                      {note.tanggal_rencana && <p className="mt-2 text-xs text-blue-300">Rencana: {note.tanggal_rencana}</p>}
                      <p className="mt-2 text-[10px] text-slate-500">Oleh: {note.created_by || "-"} · Korwil {note.korwil || d.korwil || "-"}</p>
                      {note.photo && <button type="button" onClick={() => viewProgressPhoto(note.id)} className="mt-2 inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1.5 text-xs text-blue-300 hover:bg-slate-800"><ImageIcon className="h-3.5 w-3.5" /> Lihat Foto Bukti{note.photo_name ? `: ${note.photo_name}` : ""}</button>}
                    </article>
                  ))}
                </div>
                {photoPreview && (
                  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" onClick={() => { URL.revokeObjectURL(photoPreview); setPhotoPreview(""); }}>
                    <button type="button" className="absolute right-5 top-5 text-white" onClick={() => { URL.revokeObjectURL(photoPreview); setPhotoPreview(""); }}><X className="h-6 w-6" /></button>
                    <img src={photoPreview} alt="Foto bukti kendala instalasi" className="max-h-[85vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
                  </div>
                )}
              </section>
            )}

            <h4 className="mt-6 mb-2 font-display text-base font-semibold text-white">
              Unit Instalasi ({d.units.length}) — 1 id_transaksi = 1 unit
            </h4>
            <div className="overflow-x-auto rounded border border-slate-800">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr>
                    <Th>id_transaksi</Th>
                    <Th>Unit</Th>
                    <Th>Jadwal</Th>
                    <Th>Teknisi Aktual</Th>
                    <Th>Status</Th>
                    <Th>BAPP</Th>
                  </tr>
                </thead>
                <tbody>
                  {d.units.map((u) => (
                    <tr key={u.id_transaksi} className="border-b border-slate-800/70">
                      <Td className="font-mono-data text-xs text-blue-300">{u.id_transaksi}</Td>
                      <Td className="font-mono-data text-xs">{u.unit}</Td>
                      <Td className="font-mono-data text-xs">{fmtDate(u.jadwal)}</Td>
                      <Td className="text-xs">{u.ops?.aktual_nama || <span className="text-red-300">Belum ada</span>}</Td>
                      <Td><StatusBadge value={u.ops?.status} /></Td>
                      <Td><StatusBadge value={u.ops?.bapp_status} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Sekolah() {
  const { korwilFilter } = useAuth();
  const [opts, setOpts] = useState({});
  const { draft: f, applied: appliedF, set, apply, reset } = useDeferredFilters({ kabupaten: "", q: "" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [res, setRes] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get("/filters/options").then(({ data }) => setOpts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { ...f, korwil: korwilFilter || undefined, page, per_page: perPage };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    api
      .get("/sekolah", { params })
      .then(({ data }) => setRes(data))
      .catch(() => setRes({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [appliedF, korwilFilter, page, perPage]);

  const applyFilters = () => { setPage(1); apply(); };

  return (
    <div data-testid="page-sekolah">
      <SectionTitle
        title="Database Sekolah"
        subtitle="Jumlah unit per sekolah bervariasi — tidak diasumsikan 3 unit"
        right={
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="rounded border border-blue-200 bg-white px-4 py-2 shadow-sm">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Sekolah</div>
              <div data-testid="total-sekolah" className="font-mono-data text-lg font-semibold text-blue-700">
                {(res.total || 0).toLocaleString("id-ID")}
              </div>
            </div>
          </div>
        }
      />
      <FilterBar>
        <TextFilter label="Cari Sekolah / NPSN" testId="sekolah-search" value={f.q} onChange={set("q")} placeholder="Nama sekolah / NPSN" />
        <SelectFilter label="Kabupaten/Kota" testId="sekolah-filter-kabupaten" value={f.kabupaten} onChange={set("kabupaten")} options={opts.kabupaten_unit} />
        <button
          data-testid="sekolah-reset"
          onClick={reset}
          className="rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          Reset
        </button>
      
        <SearchButton onClick={applyFilters} testId="sekolah-search-button" /></FilterBar>

      <TableShell testId="sekolah-table" loading={loading} empty={res.items.length === 0}>
        <thead>
          <tr>
            <Th>NPSN</Th>
            <Th>Nama Sekolah</Th>
            <Th>Provinsi</Th>
            <Th>Kabupaten/Kota</Th>
            <Th>Korwil</Th>
            <Th>Korda</Th>
            <Th className="text-right">Total Unit</Th>
            <Th className="text-right">Ada Teknisi</Th>
            <Th className="text-right">Tanpa Teknisi</Th>
            <Th className="w-[140px]">Progress</Th>
          </tr>
        </thead>
        <tbody>
          {res.items.map((s) => (
            <tr
              key={s.npsn}
              data-testid={`sekolah-row-${s.npsn}`}
              onClick={() => setSel(s.npsn)}
              className="cursor-pointer border-b border-slate-800/70 transition-colors hover:bg-slate-800/50"
            >
              <Td className="font-mono-data text-xs text-blue-300">{s.npsn}</Td>
              <Td className="max-w-[240px] truncate text-slate-100">{s.sekolah}</Td>
              <Td className="text-xs">{s.propinsi}</Td>
              <Td className="text-xs">{s.kabupaten_kota}</Td>
              <Td><KorwilBadge value={s.korwil} /></Td>
              <Td className="text-xs">{s.korda || "-"}</Td>
              <Td className="text-right font-mono-data text-xs">{s.total_unit}</Td>
              <Td className="text-right font-mono-data text-xs text-emerald-300">{s.unit_dengan_teknisi}</Td>
              <Td className="text-right font-mono-data text-xs text-red-300">{s.unit_tanpa_teknisi}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <Progress value={s.progress} />
                  <span className="font-mono-data text-[11px] text-slate-400">{s.progress}%</span>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableShell>
      <Pagination page={res.page || page} perPage={perPage} total={res.total} onPage={setPage} onPerPage={(n) => { setPerPage(n); setPage(1); }} testId="sekolah-pagination" />
      {sel && <SekolahDetail npsn={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
